import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Checkbox,
  IconButton,
  Grid2,
  FormGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableRow,
  TableCell,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import { IoPeople, IoMail } from "react-icons/io5";
import { FaCommentSms, FaHelmetSafety, FaLine } from "react-icons/fa6";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { MailInfo } from "IF/interfaceType";
import { maskString } from "Utils/format";
import { useThemeMode } from "Contexts/ThemeContext";

interface MobileMailProps {
  row: MailInfo;
  index: number;
  isEditing: boolean;
  currentData: MailInfo;
  width: number;
  handleChange: (
    index: number,
    field: keyof MailInfo,
    value: string | boolean
  ) => void;
  handleCheckboxChange: (
    index: number,
    field: keyof MailInfo
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveRow: (index: number) => void;
  handleCancelRow: (index: number) => void;
  handleDelete: (index: number) => void;
  startEditingRow: (index: number) => void;
  openModal: (message: string, confirmAction: () => void) => void;
}

const SettingMailMobile: React.FC<MobileMailProps> = ({
  row,
  index,
  isEditing,
  currentData,
  width,
  handleChange,
  handleCheckboxChange,
  handleSaveRow,
  handleCancelRow,
  handleDelete,
  startEditingRow,
  openModal,
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  const handleOpenModal = (id: number) => {
    openModal("選択した配信情報を削除します。よろしいですか？", () =>
      handleDelete?.(id)
    );
  };

  return (
    <Card
      sx={{
        flexGrow: 1,
        bgcolor: theme.palette.custom.row,
        gap: 1,
        maxWidth: "100%",
      }}
    >
      <CardContent>
        <Table>
          <TableBody>
            {/* 氏名 */}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                colSpan={1}
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 1,
                }}
              >
                <ListItem sx={{ margin: "0px", padding: "0px" }}>
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      mr: 0.5,
                    }}
                  >
                    <IoPeople style={{ fontSize: "2.1em" }} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      "& .MuiTypography-root": {
                        mb: 0,
                        fontSize: "1rem", // フォントサイズを小さく設定
                      },
                    }}
                    primary="氏名"
                  />
                </ListItem>
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="left"
                size="small"
                colSpan={2}
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  width: "100%",
                  p: 1,
                  pt: 0,
                }}
              >
                <Typography>{row.mailName}</Typography>
              </TableCell>
            </TableRow>
            {/* メール配信ヘッダ*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 1,
                  pb: 0,
                  width: "40%",
                }}
              >
                <ListItem sx={{ margin: "0px", padding: "0px" }}>
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      mr: 0.5,
                    }}
                  >
                    <IoMail style={{ fontSize: "2.1em" }} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      "& .MuiTypography-root": {
                        mb: 0,
                        fontSize: "1rem", // フォントサイズを小さく設定
                      },
                    }}
                    primary="メール配信"
                  />
                </ListItem>
              </TableCell>
            </TableRow>
            {/* メール配信チェックボックス*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                colSpan={2}
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 1,
                  pt: 0,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                  }}
                >
                  {isEditing ? (
                    <FormGroup row>
                      <FormControlLabel
                        label="警報"
                        control={
                          <Checkbox
                            checked={currentData.alertCheckbox}
                            disabled={!row.mailAddress}
                            size="small"
                            onChange={handleCheckboxChange(
                              index,
                              "alertCheckbox"
                            )}
                          />
                        }
                      />
                      <FormControlLabel
                        label="通信不可"
                        control={
                          <Checkbox
                            checked={currentData.connectCheckbox}
                            disabled={!row.mailAddress}
                            size="small"
                            onChange={handleCheckboxChange(
                              index,
                              "connectCheckbox"
                            )}
                          />
                        }
                      />
                    </FormGroup>
                  ) : (
                    <FormGroup row>
                      <FormControlLabel
                        label="警報"
                        control={
                          <Checkbox
                            checked={row.alertCheckbox}
                            size="small"
                            disabled
                            aria-label={`警報のみチェックボックス-${index}`}
                          />
                        }
                      />
                      <FormControlLabel
                        label="通信不可"
                        control={
                          <Checkbox
                            checked={row.connectCheckbox}
                            size="small"
                            disabled
                            aria-label={`通信不可チェックボックス-${index}`}
                          />
                        }
                      />
                    </FormGroup>
                  )}
                  {/* メールアドレス */}
                  <Typography>{row.mailAddress}</Typography>
                </Box>
              </TableCell>
            </TableRow>
            {/* SMSヘッダ*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 1,
                  pb: 0,
                  width: "40%",
                }}
              >
                <ListItem sx={{ margin: "0px", padding: "0px" }}>
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      mr: 0.5,
                    }}
                  >
                    <FaCommentSms style={{ fontSize: "2.1em" }} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      "& .MuiTypography-root": {
                        mb: 0,
                        fontSize: "1rem",
                      },
                    }}
                    primary="SMS配信"
                  />
                </ListItem>
              </TableCell>
            </TableRow>
            {/* 電話番号*/}
            <TableRow
              sx={{
                "& td, & th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                scope="row"
                align="center"
                size="small"
                colSpan={2}
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightRegular",
                  p: 1,
                  pt: 0,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pt: 0,
                    alignItems: "start",
                  }}
                >
                  {isEditing ? (
                    <FormGroup row>
                      <FormControlLabel
                        label="警報"
                        control={
                          <Checkbox
                            checked={currentData.smsCheckbox}
                            disabled={!row.telNumber}
                            onChange={handleCheckboxChange(
                              index,
                              "smsCheckbox"
                            )}
                            aria-label={`SMSチェックボックス-${index}`}
                          />
                        }
                      />
                    </FormGroup>
                  ) : (
                    <FormGroup row>
                      <FormControlLabel
                        label="警報"
                        control={
                          <Checkbox
                            checked={row.smsCheckbox}
                            disabled
                            aria-label={`SMSチェックボックス-${index}`}
                          />
                        }
                      />
                    </FormGroup>
                  )}
                  {isEditing ? (
                    <Typography>{row.telNumber}</Typography>
                  ) : (
                    <Typography>{maskString(row.telNumber)}</Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          {isEditing ? (
            <>
              <Tooltip title={"保存"} placement="top" arrow>
                <IconButton
                  onClick={() => handleSaveRow(index)}
                  aria-label="save"
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"キャンセル"} placement="top" arrow>
                <IconButton
                  onClick={() => handleCancelRow(index)}
                  aria-label="cancel"
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={"編集"} placement="top" arrow>
                <IconButton
                  onClick={() => startEditingRow(index)}
                  aria-label="edit"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"削除"} placement="top" arrow>
                <IconButton
                  onClick={() => handleOpenModal(index)}
                  aria-label="delete"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SettingMailMobile;
